import { $Props } from '@Ravnur-Inc/core/typings/tsx';
import { Vue, prop } from 'vue-class-component';

import './rav-table.scss';

const CN = 'rav-table';

export type Align = 'left' | 'center' | 'right';
export type TableView = 'striped' | 'blank';

export interface TableCellDescription<E extends Dictionary<unknown>, K = string & keyof E> {
  key: K | 'actions';
  label: string;
  align?: Align;
}

class Props<E extends Dictionary<unknown> = Dictionary<unknown>> {
  descriptions: Array<TableCellDescription<E>>;
  list: E[];
  clickable = prop<boolean>({ default: false });
  mode = prop<TableView>({ default: 'striped' });
}

type Emits = {
  onRowClick: (item: any) => void;
};

export default class Table<T extends Dictionary<unknown>> extends Vue.with(Props) {
  declare $props: $Props<Props, Emits>;
  declare list: T[];

  render() {
    return (
      <table class={[CN, `${CN}--${this.mode}`]}>
        <thead>
          <tr>{this.descriptions.map(this.th)}</tr>
        </thead>
        <tbody class={`${CN}__body`}>{this.list.map(this.tr)}</tbody>
      </table>
    );
  }

  private th({ align, label }: TableCellDescription<T>) {
    return <th class={[`${CN}__th`, `${CN}__th--${align}`]}>{label}</th>;
  }

  private tr(item: T, idx: number) {
    const { descriptions } = this;
    const clickEvent = this.clickable ? { onClick: () => this.$emit('row-click', item) } : {};
    const cn = this.clickable ? `${CN}__clickable-row` : '';

    return (
      <tr key={idx} {...clickEvent} class={cn}>
        {descriptions.map((desc) => this.td(desc, item))}
      </tr>
    );
  }

  private td({ align = 'left', key }: TableCellDescription<T>, item: T) {
    const fnc = this.$slots[key];
    const val = key === 'actions' ? '' : item[key];
    const data = fnc instanceof Function ? fnc({ item, [key]: val }) : val;
    const cn = [`${CN}__td`, `${CN}__td--${align}`, `${CN}__td--${key}`];
    return (
      <td key={key} class={cn}>
        {data}
      </td>
    );
  }
}
